import React, { useEffect, useMemo, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { isDoaga } from "../lib/chf";
import { api, fileUrl } from "../lib/api";
import { AuthImg } from "./AuthImg";
import {
  Users, Car, CalendarDays, Package, Folder, Briefcase,
  Bell, LogOut, Settings as SettingsIcon, Layers, LayoutDashboard, Home, Search, Command, Plus, Wallet, Menu, X, Truck, Fuel as FuelIcon, DatabaseBackup, Download, Upload as UploadIcon, UserCog, BookText, AlertTriangle, HardHat, GripVertical, ArrowUpDown, Check, MessageSquare,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { fmtRoDate } from "../lib/dateFmt";
import { useOnlinePresence } from "../context/OnlinePresenceContext";
import { NewProjectAlertBanner } from "./NewProjectAlertBanner";

const ICONS = {
  users: Users, car: Car, calendar: CalendarDays, package: Package,
  folder: Folder, briefcase: Briefcase, layers: Layers, home: Home, wallet: Wallet, truck: Truck, fuel: FuelIcon,
  Package: Package, Car: Car, Truck: Truck, AlertTriangle: AlertTriangle, HardHat: HardHat,
};

const ROUTE_FOR = {
  PERSONAL: "/personal",
  AUTOTURISME: "/autoturisme",
  REMORCI: "/remorci",
  PLANIFICATOR_CONCEDIU: "/concediu",
  INVENTAR: "/inventar",
  PROIECTE: "/proiecte",
  MANAGEMENT: "/management",
  CHIRII: "/chirii",
  AVANSURI: "/avansuri",
  PLANIFICATOR: "/planificator",
  CHIP_ALIMENTARE: "/chip-alimentare",
};

// RBAC vizibilitate sidebar pe modul, per rol
const ROLE_MODULES = {
  admin: null, // null = vede totul
  planificator: new Set([
    "PERSONAL", "AUTOTURISME", "REMORCI", "PLANIFICATOR_CONCEDIU", "PLANIFICATOR",
    "PROIECTE", "MANAGEMENT", "INVENTAR",
  ]),
  user: new Set([]), // doar Dashboard (EmployeeDashboard)
  magazioner: new Set(["INVENTAR", "AUTOTURISME", "REMORCI"]), // dashboard + Inventar + Autoturisme + Remorci
};

function isModuleVisibleForRole(moduleCode, role) {
  if (!role || role === "admin") return true;
  const allowed = ROLE_MODULES[role];
  if (!allowed) return true;
  return allowed.has(moduleCode);
}

export default function Layout() {
  const { user, company, logout, isAdmin, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Beacon monitorizare: înregistrează rubricile accesate de contul urmărit
  useEffect(() => {
    if (!isDoaga(user)) return;
    api.post("/watch/pageview", { path: location.pathname }).catch(() => {});
  }, [location.pathname, user]);
  const [modules, setModules] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [backupOpen, setBackupOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  // Sidebar reorder mode (admin-only): drag & drop pentru modulele dinamice
  const [reorderMode, setReorderMode] = useState(false);
  const [dragCode, setDragCode] = useState(null);
  const [dragOverCode, setDragOverCode] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);

  useEffect(() => {
    const onUp = () => setOnline(true);
    const onDown = () => setOnline(false);
    window.addEventListener("online", onUp);
    window.addEventListener("offline", onDown);
    return () => {
      window.removeEventListener("online", onUp);
      window.removeEventListener("offline", onDown);
    };
  }, []);

  const load = async () => {
    try {
      const m = await api.get("/modules");
      setModules(m.data || []);
      const n = await api.get("/notifications");
      setNotifs(n.data || []);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  // Mark all visible notifications as seen when bell popover opens
  const markAllSeen = async () => {
    const unseen = notifs.filter((n) => !n.seen);
    if (unseen.length === 0) return;
    try {
      await api.post("/notifications/mark-seen", {
        signatures: unseen.map((n) => n.signature).filter(Boolean),
      });
      // Optimistic update: mark them locally as seen
      setNotifs((cur) => cur.map((n) => ({ ...n, seen: true })));
    } catch {}
  };

  const unseenCount = notifs.filter((n) => !n.seen).length;

  // Admin: fetch sumar tasks per modul (cu refresh la fiecare 60s)
  const [missingSummary, setMissingSummary] = useState({});
  useEffect(() => {
    if (!isAdmin) return;
    const fetchMissing = () => api.get("/admin/missing-data-summary").then((r) => setMissingSummary(r.data || {})).catch(() => {});
    fetchMissing();
    const t = setInterval(fetchMissing, 60000);
    return () => clearInterval(t);
  }, [isAdmin]);

  // Magazioner: fetch count defecte (echipamente + autoturisme) cu refresh 30s
  const isMagazioner = user?.role === "magazioner";
  const [defectCounts, setDefectCounts] = useState({ cars: 0, equipment: 0 });
  useEffect(() => {
    if (!isMagazioner) return;
    const fetchDefects = () => api.get("/me/defective-items").then((r) => {
      setDefectCounts({
        cars: (r.data?.cars || []).length,
        equipment: (r.data?.equipment || []).length,
      });
    }).catch(() => {});
    fetchDefects();
    const t = setInterval(fetchDefects, 30000);
    return () => clearInterval(t);
  }, [isMagazioner]);

  // Billing — count pending (admin only)
  const [billingPending, setBillingPending] = useState(0);
  useEffect(() => {
    if (!isAdmin) return;
    const fetchBilling = () => api.get("/billing/todo").then((r) => {
      const s = r.data?.summary || {};
      setBillingPending((s.projects_pending || 0) + (s.regii_pending || 0));
    }).catch(() => {});
    fetchBilling();
    const t = setInterval(fetchBilling, 60000);
    return () => clearInterval(t);
  }, [isAdmin]);

  // Confirmări multi-șantier — count pendinte (admin only)
  const [multiSitePending, setMultiSitePending] = useState(0);
  useEffect(() => {
    if (!isAdmin) return;
    const fetchMS = () => api.get("/admin/multi-site-pending").then((r) => {
      setMultiSitePending((r.data?.items || []).length);
    }).catch(() => {});
    fetchMS();
    const t = setInterval(fetchMS, 60000);
    return () => clearInterval(t);
  }, [isAdmin]);

  // AI Assistant — fetch agent name (admin can rename)
  const [agentSettings, setAgentSettings] = useState({ name: "Asistent", emoji: "🤖" });
  useEffect(() => {
    api.get("/assistant/settings").then((r) => setAgentSettings(r.data)).catch(() => {});
  }, []);

  // Global Ctrl+K / Cmd+K shortcut (only for admin/planificator)
  const showSearch = !user || user.role === "admin" || user.role === "planificator";
  // Swiss Black theme: applied for non-admin roles (angajat / planificator / magazioner)
  const swissBlack = user && user.role !== "admin";
  // Eligibilitate Tagesraport: Khreis, Istratenco Serghei, Stoica Elena Lorena (sau admin)
  const [tagesPersonsEligible, setTagesPersonsEligible] = useState([]);
  useEffect(() => {
    if (!user) return;
    import("../lib/api").then(({ api }) => {
      api.get("/tagesraporte/eligible-persons")
        .then((r) => setTagesPersonsEligible((r.data || []).map((p) => p.id)))
        .catch(() => setTagesPersonsEligible([]));
    });
  }, [user]);
  const showTagesraport = isAdmin || (user?.person_id && tagesPersonsEligible.includes(user.person_id));
  useEffect(() => {
    if (!showSearch) return;
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showSearch]);

  return (
    <div className={`flex min-h-screen ${swissBlack ? "bg-black" : "bg-[var(--app-bg)]"}`}>
      {/* Mobile overlay */}
      {mobileNavOpen && (
        <button
          onClick={() => setMobileNavOpen(false)}
          className="lg:hidden fixed inset-0 z-30 bg-black/40"
          aria-label="Închide meniu"
          data-testid="mobile-nav-overlay"
        />
      )}
      {/* Sidebar */}
      <aside
        className={`fixed lg:static z-40 inset-y-0 left-0 w-64 flex flex-col transform transition-transform duration-200 ${mobileNavOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"} ${
          swissBlack
            ? "border-r border-white/10 bg-black text-white"
            : "border-r border-[var(--border)] bg-white"
        }`}
        data-testid="sidebar"
        data-theme={swissBlack ? "swiss-black" : "light"}
      >
        <div className={`px-6 py-6 flex items-center gap-3 ${swissBlack ? "border-b border-white/10" : "border-b border-[var(--border)]"}`}>
          {company?.logo_file_id ? (
            <AuthImg fileId={company.logo_file_id} className={`w-10 h-10 rounded-full object-cover ${swissBlack ? "ring-1 ring-white/20 bg-white" : ""}`} />
          ) : (
            <div className={`w-10 h-10 rounded-md grid place-items-center font-display font-extrabold ${swissBlack ? "bg-red-600 text-white" : "bg-[var(--primary)] text-white"}`}>C</div>
          )}
          <div>
            <div className={`font-display font-extrabold text-lg leading-none ${swissBlack ? "tracking-tight" : ""}`}>{company?.name || "ConVic"}</div>
            <div className={`text-[10px] mt-1 tracking-[0.2em] uppercase font-bold ${swissBlack ? "text-white/40" : "text-zinc-500 normal-case font-normal tracking-normal"}`}>
              {swissBlack ? "// Intern" : "Management Intern"}
            </div>
          </div>
        </div>

        <nav className="px-3 py-4 flex flex-col gap-1 overflow-y-auto flex-1" onClick={() => !reorderMode && setMobileNavOpen(false)}>
          {/* Admin: toggle reorder mode */}
          {isAdmin && (
            <div className="mb-2 flex items-center gap-1 px-1">
              {!reorderMode ? (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setReorderMode(true); }}
                  className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded inline-flex items-center gap-1 transition-colors ${swissBlack ? "bg-white/10 text-white/70 hover:bg-white/20" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}
                  data-testid="sidebar-reorder-toggle"
                  title="Rearanjează ordinea butoanelor"
                >
                  <ArrowUpDown size={10}/> Rearanjează
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    disabled={savingOrder}
                    onClick={async (e) => {
                      e.stopPropagation();
                      setSavingOrder(true);
                      try {
                        const codes = modules.map((m) => m.code);
                        await api.post("/modules/reorder", { order: codes });
                        toast.success("Ordinea sidebar-ului a fost salvată");
                        setReorderMode(false);
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "Eroare la salvare");
                      } finally {
                        setSavingOrder(false);
                      }
                    }}
                    className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded inline-flex items-center gap-1 bg-emerald-600 text-white hover:bg-emerald-700"
                    data-testid="sidebar-reorder-save"
                  >
                    <Check size={10}/> {savingOrder ? "Se salvează..." : "Salvează"}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setReorderMode(false); load(); }}
                    className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded ${swissBlack ? "bg-white/10 text-white/70 hover:bg-white/20" : "bg-zinc-200 text-zinc-700 hover:bg-zinc-300"}`}
                    data-testid="sidebar-reorder-cancel"
                  >
                    Anulează
                  </button>
                </>
              )}
            </div>
          )}
          <NavItem to="/" icon={LayoutDashboard} label="Acasă" testId="nav-dashboard" exact swissBlack={swissBlack} />
          {modules.filter((m) => isModuleVisibleForRole(m.code, user?.role) && m.code !== "REMORCI" && !(isDoaga(user) && m.code === "AVANSURI")).map((m) => {
            const Icon = ICONS[m.icon] || Layers;
            const route = ROUTE_FOR[m.code] || `/modul/${m.code}`;
            if (reorderMode && isAdmin) {
              // Drag & drop row
              const isDragging = dragCode === m.code;
              const isOver = dragOverCode === m.code && dragCode && dragCode !== m.code;
              return (
                <div
                  key={m.code}
                  draggable
                  onDragStart={(e) => { setDragCode(m.code); e.dataTransfer.effectAllowed = "move"; }}
                  onDragEnd={() => { setDragCode(null); setDragOverCode(null); }}
                  onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOverCode(m.code); }}
                  onDragLeave={() => setDragOverCode(null)}
                  onDrop={(e) => {
                    e.preventDefault();
                    const from = dragCode;
                    const to = m.code;
                    setDragCode(null); setDragOverCode(null);
                    if (!from || from === to) return;
                    setModules((prev) => {
                      const arr = [...prev];
                      const fi = arr.findIndex((x) => x.code === from);
                      const ti = arr.findIndex((x) => x.code === to);
                      if (fi < 0 || ti < 0) return prev;
                      const [moved] = arr.splice(fi, 1);
                      arr.splice(ti, 0, moved);
                      return arr;
                    });
                  }}
                  className={`flex items-center gap-2 px-3 py-2 rounded cursor-grab active:cursor-grabbing transition-all ${
                    swissBlack ? "bg-white/5 hover:bg-white/10 text-white" : "bg-zinc-50 hover:bg-zinc-100 text-zinc-800"
                  } ${isDragging ? "opacity-40" : ""} ${isOver ? (swissBlack ? "ring-2 ring-emerald-400" : "ring-2 ring-emerald-500 bg-emerald-50") : ""}`}
                  data-testid={`sidebar-drag-${m.code}`}
                >
                  <GripVertical size={14} className="opacity-50"/>
                  <Icon size={16}/>
                  <span className="text-sm font-medium flex-1 truncate">{m.name}</span>
                </div>
              );
            }
            const elem = (
              <NavItem
                key={m.code}
                to={route}
                icon={Icon}
                label={m.name}
                testId={`nav-${m.code.toLowerCase()}`}
                badgeCount={isAdmin ? badgeCountForModule(m.code, missingSummary) : 0}
                swissBlack={swissBlack}
              />
            );
            // Adăugăm sub-entry pentru Tagesraport imediat sub Personal.
            // Registru daune / Istoric PSA / Istoric reparații au fost mutate în /cronologie.
            if (m.code === "PERSONAL" && isAdmin) {
              return (
                <React.Fragment key={m.code}>
                  {elem}
                  {showTagesraport && (
                    <NavItem
                      to="/tagesraport"
                      icon={ICONS["BookText"] || BookText}
                      label="📝 Tagesraport"
                      testId="nav-tagesraport"
                      swissBlack={swissBlack}
                    />
                  )}
                  <NavItem
                    to="/fise-ore"
                    icon={ICONS["FileText"] || BookText}
                    label="🗂 Fișe ore"
                    testId="nav-fise-ore"
                    swissBlack={swissBlack}
                  />
                </React.Fragment>
              );
            }
            return elem;
          })}
          {isAdmin && (
            <NavItem
              to="/facturare"
              icon={ICONS["Receipt"] || Layers}
              label="📑 Facturare"
              testId="nav-facturare"
              badgeCount={billingPending}
              isPulse={billingPending > 0}
              swissBlack={swissBlack}
            />
          )}
          {(isAdmin || isMagazioner) && (
            <NavItem
              to="/echipament-alocat"
              icon={ICONS["Package"] || Layers}
              label="📦 Echipament alocat"
              testId="nav-echipament-alocat"
              swissBlack={swissBlack}
            />
          )}
          {isAdmin && (
            <NavItem
              to="/confirmari-multi-santier"
              icon={ICONS["AlertTriangle"] || Layers}
              label="⚠ Confirmări multi-șantier"
              testId="nav-confirmari-multi-santier"
              badgeCount={multiSitePending}
              isPulse={multiSitePending > 0}
              swissBlack={swissBlack}
            />
          )}
          {isMagazioner && (
            <>
              <div className={`mt-3 px-3 text-[10px] uppercase tracking-wider font-bold ${swissBlack ? "text-white/40" : "text-zinc-400 font-semibold"}`}>Sarcini magazie</div>
              <NavItem
                to="/preluat-din-inventar"
                icon={ICONS["Package"] || Layers}
                label="📤 Preluat din Inventar"
                testId="nav-preluat-din-inventar"
                swissBlack={swissBlack}
              />
              <NavItem
                to="/inventar?status=defect"
                icon={ICONS["Package"] || Layers}
                label="🔧 Defecte echipamente"
                testId="nav-defecte-echipamente"
                badgeCount={defectCounts.equipment}
                isPulse
                swissBlack={swissBlack}
              />
              <NavItem
                to="/autoturisme?status=defect"
                icon={ICONS["Car"] || Layers}
                label="🚗 Defecte autoturisme"
                testId="nav-defecte-autoturisme"
                badgeCount={defectCounts.cars}
                isPulse
                swissBlack={swissBlack}
              />
            </>
          )}
          {(isAdmin || isMagazioner || user?.person_id) && (
            <NavItem to="/tasks" icon={ICONS["folder"] || Layers} label="📋 Sarcini" testId="nav-tasks" swissBlack={swissBlack} />
          )}
          {/* Tagesraport entry pentru non-admin eligibili (admin îl vede deja sub Personal) */}
          {!isAdmin && showTagesraport && (
            <NavItem to="/tagesraport" icon={ICONS["BookText"] || BookText} label="📝 Tagesraport" testId="nav-tagesraport-user" swissBlack={swissBlack} />
          )}
          {/* Preluat din Inventar — eliminat „Preluările mele" din sidebar (per cerință user);
              magazionerul are propriul link mai sus, iar adminul folosește acum „Echipament alocat". */}
          <NavItem to="/video-explicative" icon={ICONS["video"] || BookText} label="🎬 Video explicative" testId="nav-video-explicative" swissBlack={swissBlack} />
          {isAdmin && (
            <NavItem to="/acte-firma" icon={BookText} label="🏢 Acte firmă" testId="nav-acte-firma" swissBlack={swissBlack} />
          )}
          <NavItem to="/cronologie" icon={MessageSquare} label="Cronologie" testId="nav-cronologie" swissBlack={swissBlack} />
          {isAdmin && (() => {
            const un = (user?.username || "").trim().toLowerCase();
            const fn = (user?.name || "").trim().toLowerCase();
            return un === "admin" || un === "gabi" || un === "victor" || fn.includes("vasiu");
          })() && (
            <>
            <NavItem to="/doaga-istoric" icon={MessageSquare} label="🕵️ DOAGA ISTORIC" testId="nav-doaga-istoric" swissBlack={swissBlack} />
            <NavItem to="/service-log" icon={MessageSquare} label="🔧 GARANȚII / SERVICE" testId="nav-service-log" swissBlack={swissBlack} />
            </>
          )}
          {isAdmin && (
            <NavItem
              to="/asistent"
              icon={Layers /* placeholder, we'll use emoji label */}
              label={`${agentSettings.emoji || "🤖"} ${agentSettings.name || "Asistent"}`}
              testId="nav-asistent"
              swissBlack={swissBlack}
            />
          )}
          {isAdmin && !isDoaga(user) && (
            <NavItem to="/settings" icon={SettingsIcon} label="Setări" testId="nav-settings" swissBlack={swissBlack} />
          )}
        </nav>

        <div className={`p-4 flex items-center justify-between ${swissBlack ? "border-t border-white/10" : "border-t border-[var(--border)]"}`}>
          <button
            type="button"
            onClick={() => setProfileOpen(true)}
            className={`flex-1 text-sm text-left hover:opacity-80 transition-opacity`}
            data-testid="open-my-profile-btn"
            title="Editează profilul meu (nume / parolă)"
          >
            <div className={`font-semibold flex items-center gap-1.5 ${swissBlack ? "text-white" : ""}`}>
              {user?.name || user?.username}
              <UserCog size={12} className="opacity-50"/>
            </div>
            <div className={`text-[10px] tracking-[0.2em] uppercase font-bold ${swissBlack ? "text-white/40" : "text-zinc-500 normal-case font-normal tracking-normal"}`}>{
              user?.role === "admin" ? "Administrator" :
              user?.role === "planificator" ? "Planificator" :
              user?.role === "magazioner" ? "Magazioner / Mecanic" :
              "Angajat"
            }</div>
          </button>
          <button
            data-testid="logout-btn"
            onClick={() => { logout(); navigate("/login"); }}
            className={`p-2 rounded-md transition-colors ${swissBlack ? "text-white/60 hover:bg-white/10 hover:text-red-500" : "text-zinc-600 hover:bg-zinc-100"}`}
            title="Deconectare"
          >
            <LogOut size={18} />
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col min-w-0 w-full">
        {!online && (
          <div className="bg-amber-100 text-amber-800 text-xs px-3 py-1.5 text-center font-medium border-b border-amber-200" data-testid="offline-banner">
            ⚠️ Offline — afișezi date din cache. Modificările vor eșua până când reconectezi.
          </div>
        )}
        <header className={`h-16 flex items-center px-3 sm:px-6 gap-2 sm:gap-4 ${swissBlack ? "border-b border-white/10 bg-black text-white" : "border-b border-[var(--border)] bg-white"}`}>
          <button
            onClick={() => setMobileNavOpen(true)}
            className={`lg:hidden p-2 rounded-md -ml-1 ${swissBlack ? "text-white/70 hover:bg-white/10" : "text-zinc-700 hover:bg-zinc-100"}`}
            aria-label="Deschide meniu"
            data-testid="mobile-nav-toggle"
          >
            <Menu size={22}/>
          </button>
          <div className="flex-1"/>
          {showSearch && (
            <button
              onClick={() => setPaletteOpen(true)}
              data-testid="cmdk-trigger"
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${swissBlack ? "border border-white/15 text-white/60 hover:border-white/40 hover:text-white" : "border border-[var(--border)] text-zinc-500 hover:bg-zinc-50 hover:border-zinc-300"}`}
              title="Căutare globală (Ctrl+K)"
            >
              <Search size={14}/>
              <span className="hidden sm:inline">Caută…</span>
              <kbd className={`ml-1 hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-mono border ${swissBlack ? "bg-white/5 text-white/60 border-white/10" : "bg-zinc-100 text-zinc-500 border-zinc-200"}`}>
                <Command size={10}/> K
              </kbd>
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => setBackupOpen(true)}
              data-testid="backup-trigger"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors border border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--primary-subtle)]"
              title="Backup și restaurare bază de date"
            >
              <DatabaseBackup size={14}/>
              <span className="hidden sm:inline">Backup</span>
            </button>
          )}
          {isAdmin && <OnlineUsersBadge swissBlack={swissBlack} />}
          <Popover onOpenChange={(open) => { if (open) markAllSeen(); }}>
            <PopoverTrigger asChild>
              <button data-testid="notifications-btn" className={`relative p-2 rounded-md ${swissBlack ? "hover:bg-white/10 text-white/80" : "hover:bg-zinc-100"}`}>
                <Bell size={20} />
                {unseenCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] rounded-full w-5 h-5 grid place-items-center font-semibold">
                    {unseenCount}
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-96 p-0">
              <div className="p-3 border-b border-[var(--border)] font-display font-semibold flex items-center justify-between">
                <span>Notificări</span>
                {notifs.length > 0 && (
                  <span className="text-[10px] font-normal text-zinc-500" data-testid="notifs-total">
                    {notifs.length} {notifs.length === 1 ? "totală" : "totale"}
                  </span>
                )}
              </div>
              <div className="max-h-96 overflow-y-auto">
                {notifs.length === 0 && <div className="p-6 text-sm text-zinc-500 text-center">Fără notificări</div>}
                {notifs.map((n, i) => {
                  const personTypes = ["vacation_start", "vacation_end", "vacation_pending", "person_90", "bila_rosie"];
                  const carTypes = ["mfk_expired", "revizie_due", "dpf_due"];
                  const isTransferPending = n.type === "inventory_transfer_pending" && n.share_id;
                  const target = personTypes.includes(n.type) && n.ref_id
                    ? `/personal/${n.ref_id}`
                    : carTypes.includes(n.type) && n.ref_id
                    ? `/autoturisme/${n.ref_id}`
                    : (n.type === "task_assigned" || n.type === "task_assigned_urgent" || n.type === "urgent_task_completed")
                    ? "/tasks"
                    : n.type === "inventory_shared"
                    ? "/inventar"
                    : n.type === "dismantle_assigned"
                    ? "/angajat"
                    : null;
                  const baseClass = `p-3 border-b border-[var(--border)] flex items-start gap-3 transition-colors ${n.seen ? "bg-zinc-50/40 opacity-70" : "bg-white"}`;
                  // Pentru transfer pending: NU folosim wrapper-clickable (avem butoane proprii)
                  const Wrapper = isTransferPending
                    ? ({ children }) => (
                        <div className={baseClass} data-testid={`notif-transfer-pending-${i}`}>
                          {children}
                        </div>
                      )
                    : target
                    ? ({ children }) => (
                        <button
                          type="button"
                          onClick={() => navigate(target)}
                          data-testid={`notif-link-${n.type}-${i}`}
                          className={`w-full text-left ${baseClass} hover:bg-zinc-100`}
                        >
                          {children}
                        </button>
                      )
                    : ({ children }) => (
                        <div className={baseClass}>
                          {children}
                        </div>
                      );
                  return (
                    <Wrapper key={i}>
                      <span className={`bila-dot ${n.severity === "critical" ? "bila-red bila-pulse" : "bila-orange"} ${n.seen ? "opacity-50" : ""}`} />
                      {(n.type === "inventory_shared" || isTransferPending) && n.photo_id && (
                        <img
                          src={fileUrl(n.photo_id)}
                          alt=""
                          className="w-10 h-10 rounded object-cover border border-zinc-200 flex-shrink-0"
                        />
                      )}
                      <div className="flex-1 text-sm min-w-0">
                        <div className={`${n.seen ? "font-normal text-zinc-600" : "font-semibold text-zinc-900"}`}>{n.title}</div>
                        {n.message && <div className="text-xs text-zinc-600 italic mt-0.5 line-clamp-2">„{n.message}"</div>}
                        <div className="text-xs text-zinc-500 mt-0.5">{fmtRoDate(n.date)}</div>
                        {/* Butoane Accept/Refuze pentru transfer pending */}
                        {isTransferPending && (
                          <div className="mt-2 flex gap-2" data-testid={`notif-transfer-actions-${i}`}>
                            <Button
                              size="sm"
                              className="h-7 px-3 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                              data-testid={`notif-transfer-accept-${n.share_id}`}
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  await api.post(`/inventory-shares/${n.share_id}/accept`);
                                  toast.success(`Transfer acceptat: ${n.title}`);
                                  await load();
                                } catch (err) {
                                  toast.error(err?.response?.data?.detail || "Eroare la accept");
                                }
                              }}
                            >
                              ✓ Acceptă
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-3 text-xs border-red-300 text-red-700 hover:bg-red-50"
                              data-testid={`notif-transfer-refuse-${n.share_id}`}
                              onClick={async (e) => {
                                e.stopPropagation();
                                // Primul confirm
                                if (!window.confirm(`Refuzi transferul „${n.title.replace('📦 ', '').split(' transferat')[0]}"? Echipamentul va reveni la persoana care l-a avut anterior.`)) return;
                                // Al doilea confirm — final
                                if (!window.confirm("Ești 100% sigur că nu ai primit acest echipament? Persoana care l-a trimis va fi notificată.")) return;
                                try {
                                  const r = await api.post(`/inventory-shares/${n.share_id}/refuse`);
                                  const dest = r.data?.new_assignee ? `→ ${r.data.new_assignee}` : "→ stoc";
                                  const notifiedTxt = r.data?.notified > 0 ? ` (notificat${r.data.notified === 1 ? "" : "ăți"} ${r.data.notified})` : "";
                                  toast.success(`Refuzat: echipamentul revine ${dest}${notifiedTxt}`);
                                  await load();
                                } catch (err) {
                                  toast.error(err?.response?.data?.detail || "Eroare la refuz");
                                }
                              }}
                            >
                              ✗ Refuză
                            </Button>
                          </div>
                        )}
                      </div>
                      {!n.seen && <span className="w-2 h-2 rounded-full bg-[var(--primary)] mt-1.5" data-testid={`notif-unread-${i}`} title="Necitit"/>}
                    </Wrapper>
                  );
                })}
              </div>
            </PopoverContent>
          </Popover>
        </header>

        <div className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto overflow-x-hidden" data-main-scroll="true">
          <NewProjectAlertBanner />
          <Outlet />
        </div>
      </main>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} navigate={navigate} />
      <BackupDialog open={backupOpen} onClose={() => setBackupOpen(false)} />
      <MyProfileDialog
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        user={user || undefined}
        onUpdated={async () => {
          try {
            const r = await api.get("/auth/me");
            setUser(r.data);
          } catch { /* ignore */ }
        }}
      />
    </div>
  );
}

function CommandPalette({ open, onClose, navigate }) {
  const [q, setQ] = useState("");
  const [data, setData] = useState({ persons: [], cars: [], inventory: [], projects: [], regii: [], rentals: [] });
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    if (!open) return;
    setQ("");
    setActiveIdx(0);
    (async () => {
      try {
        const [p, c, i, pr, rg, rt] = await Promise.all([
          api.get("/persons").catch(() => ({ data: [] })),
          api.get("/cars").catch(() => ({ data: [] })),
          api.get("/inventory").catch(() => ({ data: [] })),
          api.get("/projects").catch(() => ({ data: [] })),
          api.get("/regii").catch(() => ({ data: [] })),
          api.get("/rentals").catch(() => ({ data: [] })),
        ]);
        setData({
          persons: p.data || [],
          cars: c.data || [],
          inventory: i.data || [],
          projects: pr.data || [],
          regii: rg.data || [],
          rentals: rt.data || [],
        });
      } catch {}
    })();
  }, [open]);

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) {
      // Show modules as quick nav when no query
      return [
        { type: "nav", icon: LayoutDashboard, label: "Dashboard", to: "/" },
        { type: "nav", icon: Users, label: "Personal", to: "/personal" },
        { type: "nav", icon: Car, label: "Autoturisme", to: "/autoturisme" },
        { type: "nav", icon: Package, label: "Inventar", to: "/inventar" },
        { type: "nav", icon: Folder, label: "Proiecte și Regii", to: "/proiecte" },
        { type: "nav", icon: Briefcase, label: "Management", to: "/management" },
        { type: "nav", icon: Home, label: "Chirii", to: "/chirii" },
        { type: "nav", icon: CalendarDays, label: "Planificator Concediu", to: "/concediu" },
      ];
    }
    const out = [];
    for (const p of data.persons) {
      const hay = `${p.nume || ""} ${p.prenume || ""} ${p.telefon || ""}`.toLowerCase();
      if (hay.includes(needle)) out.push({
        type: "person", icon: Users,
        label: `${p.nume || ""} ${p.prenume || ""}`.trim() || "—",
        sub: p.telefon || (p.adresa_cazare || ""),
        to: `/personal/${p.id}`,
      });
    }
    for (const c of data.cars) {
      const hay = `${c.marca || ""} ${c.model || ""} ${c.numar_inmatriculare || ""}`.toLowerCase();
      if (hay.includes(needle)) out.push({
        type: "car", icon: Car,
        label: `${c.marca || ""} ${c.model || ""}`.trim(),
        sub: c.numar_inmatriculare || "",
        to: `/autoturisme/${c.id}`,
      });
    }
    for (const i of data.inventory) {
      const hay = `${i.name || ""} ${i.note || ""} ${i.location || ""}`.toLowerCase();
      if (hay.includes(needle)) out.push({
        type: "item", icon: Package,
        label: i.name,
        sub: `${i.location || "—"}${i.shelf_row && i.shelf_col ? ` · ${i.shelf_row}${i.shelf_col}` : ""}${i.quantity ? ` · ${i.quantity}${i.unit ? " " + i.unit : ""}` : ""}`,
        to: "/inventar",
      });
    }
    for (const p of data.projects) {
      const hay = `${p.title || ""} ${p.bauherr || ""} ${p.auftraggeber || ""}`.toLowerCase();
      if (hay.includes(needle)) out.push({
        type: "project", icon: Folder,
        label: p.title || "—",
        sub: p.category || p.bauherr || "",
        to: "/proiecte",
      });
    }
    for (const r of data.regii) {
      const hay = `${r.title || ""}`.toLowerCase();
      if (hay.includes(needle)) out.push({
        type: "regie", icon: Briefcase,
        label: r.title || "—",
        sub: "Regie",
        to: "/proiecte",
      });
    }
    for (const r of data.rentals) {
      const hay = `${r.titlu || ""} ${r.adresa || ""}`.toLowerCase();
      if (hay.includes(needle)) out.push({
        type: "rental", icon: Home,
        label: r.titlu || r.adresa || "—",
        sub: r.adresa || "",
        to: "/chirii",
      });
    }
    return out.slice(0, 50);
  }, [q, data]);

  const QUICK_ACTIONS = useMemo(() => [
    { type: "action", icon: Plus, label: "Adaugă persoană nouă", sub: "Personal", to: "/personal#new" },
    { type: "action", icon: Plus, label: "Adaugă autoturism nou", sub: "Autoturisme", to: "/autoturisme#new" },
    { type: "action", icon: Plus, label: "Comandă nouă în Inventar", sub: "Inventar · echipament/piesă/consumabil", to: "/inventar#new" },
  ], []);

  const allItems = useMemo(() => {
    return [...results, ...QUICK_ACTIONS];
  }, [results, QUICK_ACTIONS]);

  const go = (r) => {
    if (!r) return;
    if (r.to.includes("#")) {
      const [path, hash] = r.to.split("#");
      navigate(path);
      // After navigation set hash so target page picks it up
      setTimeout(() => { window.location.hash = hash; }, 50);
    } else {
      navigate(r.to);
    }
    onClose();
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, allItems.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); go(allItems[activeIdx]); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl p-0 gap-0 overflow-hidden" data-testid="cmdk-palette">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-[var(--border)]">
          <Search size={16} className="text-zinc-400"/>
          <Input
            autoFocus
            value={q}
            onChange={(e) => { setQ(e.target.value); setActiveIdx(0); }}
            onKeyDown={onKey}
            placeholder="Caută persoane, autoturisme, inventar, proiecte…"
            className="border-0 shadow-none focus-visible:ring-0 px-0 text-sm"
            data-testid="cmdk-input"
          />
          <kbd className="text-[10px] font-mono text-zinc-400 border border-zinc-200 rounded px-1.5 py-0.5">ESC</kbd>
        </div>
        <div className="max-h-[60vh] overflow-y-auto py-2">
          {results.length === 0 && q ? (
            <div className="text-sm text-zinc-400 text-center py-6">Niciun rezultat pentru „{q}"</div>
          ) : (
            results.map((r, i) => {
              const Icon = r.icon || Layers;
              return (
                <button
                  key={`${r.type}-${i}`}
                  onMouseEnter={() => setActiveIdx(i)}
                  onClick={() => go(r)}
                  data-testid={`cmdk-result-${i}`}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${i === activeIdx ? "bg-[var(--primary-subtle)]" : "hover:bg-zinc-50"}`}
                >
                  <div className={`w-8 h-8 rounded-md grid place-items-center flex-shrink-0 ${i === activeIdx ? "bg-[var(--primary)] text-white" : "bg-zinc-100 text-zinc-500"}`}>
                    <Icon size={16}/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{r.label}</div>
                    {r.sub && <div className="text-xs text-zinc-500 truncate">{r.sub}</div>}
                  </div>
                  <span className="text-[10px] text-zinc-400 uppercase font-mono">{r.type === "nav" ? "modul" : r.type}</span>
                </button>
              );
            })
          )}

          <div className="px-4 py-2 mt-1 border-t border-[var(--border)] bg-zinc-50/60 text-[10px] uppercase font-mono tracking-wider text-zinc-500">Acțiuni rapide</div>
          {QUICK_ACTIONS.map((r, j) => {
            const i = results.length + j;
            const Icon = r.icon || Plus;
            return (
              <button
                key={`action-${j}`}
                onMouseEnter={() => setActiveIdx(i)}
                onClick={() => go(r)}
                data-testid={`cmdk-action-${j}`}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${i === activeIdx ? "bg-[var(--primary-subtle)]" : "hover:bg-zinc-50"}`}
              >
                <div className={`w-8 h-8 rounded-md grid place-items-center flex-shrink-0 ${i === activeIdx ? "bg-[var(--primary)] text-white" : "bg-[var(--green-bg)] text-[var(--green-valid)]"}`}>
                  <Icon size={16}/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{r.label}</div>
                  {r.sub && <div className="text-xs text-zinc-500 truncate">{r.sub}</div>}
                </div>
                <span className="text-[10px] text-zinc-400 uppercase font-mono">acțiune</span>
              </button>
            );
          })}
        </div>
        <div className="px-4 py-2 border-t border-[var(--border)] bg-zinc-50 flex items-center gap-3 text-[10px] text-zinc-500">
          <span><kbd className="px-1 py-0.5 rounded bg-white border border-zinc-200 font-mono">↑↓</kbd> navigare</span>
          <span><kbd className="px-1 py-0.5 rounded bg-white border border-zinc-200 font-mono">↵</kbd> deschide</span>
          <span><kbd className="px-1 py-0.5 rounded bg-white border border-zinc-200 font-mono">Esc</kbd> închide</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function badgeCountForModule(code, summary) {
  if (!summary) return 0;
  switch (code) {
    case "PERSONAL":   return summary.persons || 0;
    case "AUTOTURISME": return summary.cars || 0;
    case "PROIECTE":   return (summary.projects || 0) + (summary.regii || 0);
    default: return 0;
  }
}

function NavItem({ to, icon: Icon, label, testId, exact, badgeCount, isPulse, swissBlack }) {
  return (
    <NavLink
      to={to}
      end={exact}
      data-testid={testId}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors relative ${
          swissBlack
            ? isActive
              ? "bg-red-600 text-white"
              : "text-white/70 hover:bg-white/5 hover:text-white"
            : isActive
              ? "bg-[var(--primary-subtle)] text-[var(--primary)]"
              : "text-zinc-700 hover:bg-zinc-100"
        }`
      }
    >
      <span className="relative inline-flex">
        <Icon size={18} />
        {isPulse && badgeCount > 0 && (
          <>
            <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-red-500 animate-ping" />
            <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-red-500" />
          </>
        )}
      </span>
      <span className="flex-1">{label}</span>
      {badgeCount > 0 && (
        <span
          className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white animate-pulse min-w-[20px] text-center ${isPulse ? "bg-red-500" : "bg-amber-500"}`}
          title={`${badgeCount} ${isPulse ? "defect" + (badgeCount === 1 ? "" : "e") : (badgeCount === 1 ? "task" : "task-uri")}`}
          data-testid={`${testId}-badge`}
        >
          {badgeCount > 99 ? "99+" : badgeCount}
        </span>
      )}
    </NavLink>
  );
}

// ---------- BackupDialog (admin only) ----------
function BackupDialog({ open, onClose }) {
  const [downloading, setDownloading] = React.useState(false);
  const [downloadingCode, setDownloadingCode] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [confirming, setConfirming] = React.useState(null); // file ready to upload

  const downloadBackup = async () => {
    setDownloading(true);
    try {
      const r = await api.get("/admin/full-db-backup", { responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.download = `convic-fullbackup-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Backup descărcat cu succes");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la descărcarea backup-ului");
    } finally {
      setDownloading(false);
    }
  };

  const downloadCodeBackup = async () => {
    setDownloadingCode(true);
    try {
      const r = await api.get("/admin/code-backup", { responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.download = `convic-code-backup-${ts}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      const fileCount = r.headers?.["x-file-count"];
      toast.success(`Cod sursă descărcat cu succes${fileCount ? ` · ${fileCount} fișiere` : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la descărcarea codului");
    } finally {
      setDownloadingCode(false);
    }
  };

  const submitRestore = async () => {
    if (!confirming) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", confirming);
      const r = await api.post("/admin/full-db-restore", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      const det = r.data;
      toast.success(`Restaurare reușită: ${det.collections_restored} colecții, ${det.documents_restored} documente`, { duration: 8000 });
      setConfirming(null);
      onClose();
      // Force reload so the UI reflects the new DB state
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la restaurare");
    } finally {
      setUploading(false);
    }
  };

  // Weekly auto backup config
  const [weeklyCfg, setWeeklyCfg] = React.useState(null);
  const [weeklySaving, setWeeklySaving] = React.useState(false);
  const [weeklyTesting, setWeeklyTesting] = React.useState(false);
  React.useEffect(() => {
    if (!open) return;
    api.get("/admin/weekly-backup-email").then((r) => setWeeklyCfg(r.data)).catch(() => {});
  }, [open]);
  const saveWeekly = async () => {
    if (!weeklyCfg) return;
    setWeeklySaving(true);
    try {
      const r = await api.put("/admin/weekly-backup-email", { enabled: weeklyCfg.enabled, to_email: weeklyCfg.to_email });
      setWeeklyCfg(r.data);
      toast.success("Configurare salvată");
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare salvare"); }
    finally { setWeeklySaving(false); }
  };
  const [weeklyHistory, setWeeklyHistory] = React.useState(null);
  const [showHistory, setShowHistory] = React.useState(false);
  const loadHistory = async () => {
    try {
      const r = await api.get("/admin/weekly-backup-email/history");
      setWeeklyHistory(r.data.items || []);
      setShowHistory(true);
    } catch { toast.error("Eroare încărcare istoric"); }
  };
  const testWeekly = async () => {
    setWeeklyTesting(true);
    try {
      const r = await api.post("/admin/weekly-backup-email/run-now");
      toast.success(`Email trimis · DB ${Math.round((r.data.db_size || 0) / 1024)} KB · cod ${Math.round((r.data.code_size || 0) / 1024)} KB`);
      const cfg = await api.get("/admin/weekly-backup-email");
      setWeeklyCfg(cfg.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare la trimitere"); }
    finally { setWeeklyTesting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !uploading) { setConfirming(null); onClose(); } }}>
      <DialogContent className="max-w-lg" data-testid="backup-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DatabaseBackup size={20} className="text-[var(--primary)]"/> Backup &amp; Restaurare bază de date
          </DialogTitle>
        </DialogHeader>

        {!confirming ? (
          <div className="space-y-4">
            <div className="text-sm text-zinc-600 leading-relaxed">
              Descarcă întreaga bază de date a platformei într-un singur fișier JSON sau încarcă un backup
              anterior pentru a restaura starea platformei.
            </div>

            <div className="grid grid-cols-1 gap-3">
              <button
                onClick={downloadBackup}
                disabled={downloading || uploading || downloadingCode}
                data-testid="backup-download-btn"
                className="card-flat p-4 text-left hover:border-[var(--primary)] transition-colors flex items-start gap-3 disabled:opacity-60 disabled:cursor-wait"
              >
                <div className="w-10 h-10 rounded-md bg-[var(--primary-subtle)] text-[var(--primary)] grid place-items-center flex-shrink-0">
                  <Download size={20}/>
                </div>
                <div className="flex-1">
                  <div className="font-display text-base font-bold">
                    {downloading ? "Se descarcă…" : "Descarcă backup bază de date"}
                  </div>
                  <div className="text-xs text-zinc-500 mt-1">
                    Fișier JSON cu toate colecțiile (persoane, proiecte, autoturisme, inventar, ore, etc.).
                  </div>
                </div>
              </button>

              <button
                onClick={downloadCodeBackup}
                disabled={downloading || uploading || downloadingCode}
                data-testid="backup-download-code-btn"
                className="card-flat p-4 text-left hover:border-emerald-500 transition-colors flex items-start gap-3 disabled:opacity-60 disabled:cursor-wait"
              >
                <div className="w-10 h-10 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 grid place-items-center flex-shrink-0">
                  <Download size={20}/>
                </div>
                <div className="flex-1">
                  <div className="font-display text-base font-bold">
                    {downloadingCode ? "Se generează ZIP…" : "Descarcă cod sursă platformă (ZIP)"}
                  </div>
                  <div className="text-xs text-zinc-500 mt-1">
                    Tot codul aplicației (backend + frontend) într-un ZIP. Util ca backup off-platform sau pentru migrare/audit. <em>Exclude .env, node_modules, .git, build/.</em>
                  </div>
                </div>
              </button>

              <label
                className={`card-flat p-4 text-left transition-colors flex items-start gap-3 ${uploading || downloading || downloadingCode ? "opacity-60 cursor-wait" : "hover:border-amber-500 cursor-pointer"}`}
                data-testid="backup-upload-label"
              >
                <div className="w-10 h-10 rounded-md bg-amber-50 text-amber-700 border border-amber-200 grid place-items-center flex-shrink-0">
                  <UploadIcon size={20}/>
                </div>
                <div className="flex-1">
                  <div className="font-display text-base font-bold">Încarcă backup pentru restaurare</div>
                  <div className="text-xs text-zinc-500 mt-1">
                    Selectează un fișier JSON exportat anterior din această platformă.
                  </div>
                </div>
                <input
                  type="file"
                  accept="application/json,.json"
                  className="hidden"
                  disabled={uploading || downloading}
                  data-testid="backup-upload-input"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) setConfirming(f);
                    e.target.value = "";
                  }}
                />
              </label>
            </div>

            <div className="text-[11px] text-zinc-500 bg-zinc-50 border border-zinc-200 rounded p-2">
              <strong>Recomandare:</strong> descarcă un backup chiar înainte de a restaura, pentru siguranță.
            </div>

            {/* Backup săptămânal automat (Resend) */}
            {weeklyCfg && (
              <div className="card-flat p-4 bg-blue-50 border border-blue-200" data-testid="weekly-backup-section">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="font-display font-bold text-base text-blue-900">📧 Backup săptămânal pe email</div>
                    <div className="text-xs text-blue-700 mt-0.5">{weeklyCfg.schedule} · DB JSON + cod sursă ZIP</div>
                  </div>
                  <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={!!weeklyCfg.enabled}
                      onChange={(e) => setWeeklyCfg({ ...weeklyCfg, enabled: e.target.checked })}
                      className="w-5 h-5 accent-[var(--primary)]"
                      data-testid="weekly-backup-enabled"
                    />
                    <span className="text-sm font-bold text-blue-900">Activează</span>
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="email1@firma.ro, email2@firma.ro, ..."
                    value={weeklyCfg.to_email || ""}
                    onChange={(e) => setWeeklyCfg({ ...weeklyCfg, to_email: e.target.value })}
                    className="flex-1 h-9 px-3 rounded-md border border-blue-300 bg-white text-sm"
                    data-testid="weekly-backup-email-input"
                  />
                  <Button size="sm" onClick={saveWeekly} disabled={weeklySaving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="weekly-backup-save">
                    {weeklySaving ? "Se salvează…" : "💾 Salvează"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={testWeekly} disabled={weeklyTesting || !weeklyCfg.enabled || !weeklyCfg.to_email} data-testid="weekly-backup-test">
                    {weeklyTesting ? "Se trimite…" : "✉ Trimite test"}
                  </Button>
                </div>
                <div className="text-[10px] text-blue-600 mt-1 italic">
                  💡 Poți adăuga mai multe adrese separate prin virgulă pentru redundanță (ex: admin + contabil + IT)
                </div>
                {weeklyCfg.last_run_at && (
                  <div className="text-[11px] text-blue-700 mt-2 pt-2 border-t border-blue-200">
                    {weeklyCfg.last_run_ok ? "✅" : "❌"} Ultimul {weeklyCfg.last_run_reason === "manual" ? "test" : "rulaj"}: {weeklyCfg.last_run_at?.slice(0, 16).replace("T", " ")}
                    {weeklyCfg.last_run_ok && weeklyCfg.last_db_size ? ` · DB ${Math.round(weeklyCfg.last_db_size / 1024)} KB · cod ${Math.round((weeklyCfg.last_code_size || 0) / 1024)} KB` : ""}
                    {weeklyCfg.last_run_error ? <div className="text-red-700 mt-0.5">Eroare: {weeklyCfg.last_run_error}</div> : null}
                  </div>
                )}
                <button
                  onClick={loadHistory}
                  className="text-[11px] text-blue-600 hover:underline mt-2 font-semibold"
                  data-testid="weekly-backup-history-toggle"
                >
                  📜 Vezi istoric ({weeklyCfg.last_run_at ? "ultimele rulări" : "fără rulări încă"}) →
                </button>
                {showHistory && weeklyHistory && (
                  <div className="mt-2 max-h-48 overflow-y-auto bg-white border border-blue-200 rounded">
                    {weeklyHistory.length === 0 ? (
                      <div className="p-3 text-xs text-zinc-500 italic text-center">Niciun backup trimis încă.</div>
                    ) : (
                      <table className="w-full text-[11px]">
                        <thead className="bg-blue-100 sticky top-0">
                          <tr>
                            <th className="p-1.5 text-left font-bold text-blue-900">Data</th>
                            <th className="p-1.5 text-center font-bold text-blue-900">Stare</th>
                            <th className="p-1.5 text-left font-bold text-blue-900">Motiv</th>
                            <th className="p-1.5 text-left font-bold text-blue-900">Destinatari</th>
                            <th className="p-1.5 text-right font-bold text-blue-900">Mărime</th>
                          </tr>
                        </thead>
                        <tbody>
                          {weeklyHistory.map((h) => (
                            <tr key={h.id} className="border-b border-blue-100 last:border-b-0">
                              <td className="p-1.5">{h.ran_at?.slice(0, 16).replace("T", " ")}</td>
                              <td className="p-1.5 text-center">{h.ok ? "✅" : "❌"}</td>
                              <td className="p-1.5">{h.reason === "manual" ? "Test" : "Auto"}</td>
                              <td className="p-1.5 truncate max-w-[140px]" title={(h.recipients || []).join(", ")}>
                                {(h.recipients || []).length}× {(h.recipients || [])[0] || ""}
                              </td>
                              <td className="p-1.5 text-right tabular-nums">
                                {Math.round(((h.db_size || 0) + (h.code_size || 0)) / 1024)} KB
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">
              <div className="font-bold mb-1">⚠️ Atenție — restaurare bază de date</div>
              <div className="text-xs">
                Toate datele actuale din colecțiile incluse în backup vor fi <b>înlocuite definitiv</b> cu cele din fișier.
                Această operațiune nu poate fi anulată.
              </div>
            </div>
            <div className="text-sm">
              Fișier selectat:
              <div className="mt-1 px-3 py-2 rounded border border-zinc-200 bg-zinc-50 font-mono text-xs">
                📄 {confirming.name} <span className="text-zinc-400">({(confirming.size / 1024).toFixed(1)} KB)</span>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          {confirming ? (
            <>
              <Button variant="outline" onClick={() => setConfirming(null)} disabled={uploading}>Anulează</Button>
              <Button
                onClick={submitRestore}
                disabled={uploading}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="backup-restore-confirm"
              >
                {uploading ? "Se restaurează…" : "Confirmă restaurarea"}
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={onClose}>Închide</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function MyProfileDialog({ open, onClose, user, onUpdated }) {
  const [name, setName] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && user) {
      setName(user.name || user.username || "");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    }
  }, [open, user]);

  if (!open) return null;

  const save = async () => {
    if (!name.trim()) {
      toast.error("Numele nu poate fi gol");
      return;
    }
    if (newPassword) {
      if (newPassword.length < 8) {
        toast.error("Parola nouă trebuie să aibă minim 8 caractere");
        return;
      }
      if (newPassword !== confirmPassword) {
        toast.error("Parolele nu coincid");
        return;
      }
      if (!currentPassword) {
        toast.error("Confirmă parola curentă");
        return;
      }
    }
    const payload = { name: name.trim() };
    if (newPassword) {
      payload.current_password = currentPassword;
      payload.new_password = newPassword;
    }
    setSaving(true);
    try {
      await api.put("/auth/me", payload);
      toast.success("Profil actualizat");
      onUpdated && onUpdated();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="my-profile-dialog">
        <DialogHeader>
          <DialogTitle>👤 Profilul meu</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-zinc-700 block mb-1">Nume afișat</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="ex: Victor Vasiu"
              data-testid="my-profile-name"
            />
            <div className="text-[10px] text-zinc-500 mt-1">
              Username pentru autentificare: <strong>{user?.username}</strong> (nu se poate schimba)
            </div>
          </div>
          <div className="border-t border-zinc-200 pt-3">
            <div className="text-xs font-bold uppercase tracking-wider text-zinc-700 mb-2">Schimbă parola (opțional)</div>
            <div className="space-y-2">
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Parola curentă"
                data-testid="my-profile-current-password"
              />
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Parolă nouă (min. 8 caractere)"
                data-testid="my-profile-new-password"
              />
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirmă parola nouă"
                data-testid="my-profile-confirm-password"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Anulează</Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="my-profile-save"
          >
            {saving ? "Se salvează…" : "Salvează"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



// ---------- OnlineUsersBadge (admin header) ----------
function formatLastSeen(iso) {
  if (!iso) return "niciodată";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "acum câteva secunde";
    if (diffMin < 60) return `acum ${diffMin} min`;
    const diffH = Math.floor(diffMin / 60);
    const hhmm = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    if (diffH < 24 && d.toDateString() === now.toDateString()) return `azi ${hhmm}`;
    const y = new Date(now); y.setDate(y.getDate() - 1);
    if (d.toDateString() === y.toDateString()) return `ieri ${hhmm}`;
    const diffD = Math.floor(diffH / 24);
    if (diffD < 7) return `acum ${diffD} zile`;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}.${mm}.${d.getFullYear()} ${hhmm}`;
  } catch {
    return "—";
  }
}

function OnlineUsersBadge({ swissBlack }) {
  const { details, onlineUserIds } = useOnlinePresence();
  const { user: currentUser } = useAuth();
  const [allUsers, setAllUsers] = useState([]);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const onlineCount = onlineUserIds.size;

  // Fetch toți utilizatorii când se deschide popover-ul (refresh la fiecare deschidere)
  useEffect(() => {
    if (!popoverOpen) return;
    let cancelled = false;
    api.get("/users").then((r) => {
      if (!cancelled) setAllUsers(r.data || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [popoverOpen]);

  // Sortare: online primii, apoi după last_seen descrescător (cei mai recenți offline mai sus)
  const sortedUsers = useMemo(() => {
    const arr = [...(allUsers || [])];
    arr.sort((a, b) => {
      const aOnline = onlineUserIds.has(a.id) ? 1 : 0;
      const bOnline = onlineUserIds.has(b.id) ? 1 : 0;
      if (aOnline !== bOnline) return bOnline - aOnline;
      const aTs = a.last_seen ? new Date(a.last_seen).getTime() : 0;
      const bTs = b.last_seen ? new Date(b.last_seen).getTime() : 0;
      if (aTs !== bTs) return bTs - aTs;
      return String(a.name || a.username || "").localeCompare(String(b.name || b.username || ""));
    });
    return arr;
  }, [allUsers, onlineUserIds]);

  return (
    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="online-users-badge"
          title={`${onlineCount} ${onlineCount === 1 ? "utilizator conectat" : "utilizatori conectați"}`}
          className={`relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-sm font-semibold transition-colors ${
            swissBlack
              ? "border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"
              : "border border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          }`}
        >
          <span className="relative inline-flex">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping absolute"/>
            <span className="w-2 h-2 rounded-full bg-emerald-500 relative"/>
          </span>
          <span className="hidden sm:inline tabular-nums">{onlineCount}</span>
          <span className="hidden md:inline text-xs font-normal opacity-80">online</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="online-users-popover">
        <div className="p-3 border-b border-[var(--border)] font-display font-semibold text-sm flex items-center justify-between">
          <span>Utilizatori</span>
          <span className="text-[10px] font-normal text-zinc-500 tabular-nums">
            {onlineCount}/{sortedUsers.length} online
          </span>
        </div>
        <div className="max-h-96 overflow-y-auto py-1">
          {sortedUsers.length === 0 ? (
            <div className="px-4 py-6 text-xs text-zinc-500 text-center">Se încarcă…</div>
          ) : (
            sortedUsers.map((u) => {
              const online = onlineUserIds.has(u.id);
              const lastSeen = details[u.id]?.last_seen || u.last_seen;
              const isMe = u.id === currentUser?.id;
              return (
                <div
                  key={u.id}
                  className="px-3 py-2 flex items-center gap-2 hover:bg-zinc-50"
                  data-testid={`presence-user-row-${u.id}`}
                  data-online={online ? "true" : "false"}
                >
                  <span className="relative inline-flex flex-shrink-0">
                    {online ? (
                      <>
                        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping absolute"/>
                        <span className="w-2 h-2 rounded-full bg-emerald-500 relative"/>
                      </>
                    ) : (
                      <span className="w-2 h-2 rounded-full bg-zinc-300"/>
                    )}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm truncate ${online ? "font-semibold text-zinc-900" : "font-medium text-zinc-600"}`}>
                      {u.name || u.username || "—"}
                      {isMe && <span className="ml-1 text-[10px] text-zinc-400 font-normal">(tu)</span>}
                    </div>
                    <div className="text-[11px] text-zinc-500 truncate">
                      {online ? (
                        <span className="text-emerald-700 font-semibold">● online</span>
                      ) : (
                        <span>offline · {formatLastSeen(lastSeen)}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
